import { useTranslation } from "react-i18next";
import { useAppContext } from "../../AppContextDef";
import RemoteParticipantView from "../RemoteParticipantView";
import useIsMobile from "../../utils/useIsMobile";
import React, { useEffect, useMemo, useState } from "react";
import MainCard from "../MainCard";
import CharacterView from "../CharacterView";
import imageSrc from "../../repeat_audio_icon.png";
import { fontMap } from "../../utils/common";

const PolicyDetailSection = ({ formattedTime, characterJoined }) => {
  const {
    allPubsubData: allDetails,
    HeaderSectionHeight,
    journeyConfigData,
    agreedData,
    currentPage,
    selectedLang,
  } = useAppContext();
  const { t, i18n } = useTranslation();
  const isMobile = useIsMobile();
  const [messageSent, setMessageSent] = useState(false);
  const [isCharacterSpeaking, setIsCharacterSpeaking] = useState(false);
  const [viewportHeight, setViewportHeight] = useState(window.innerHeight);
  const [inputValues, setInputValues] = useState([]);

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
  };

  useEffect(() => {
    changeLanguage(selectedLang?.lang);
  }, [selectedLang]);

  useEffect(() => {
    const productDetails =
      allDetails?.iciciApplicationDetails?.productDetails || [];
    const initialValues = productDetails.map((product) => ({
      planName: product.productName,
      lifeCoverSum: product.sumAssured,
      premiumAmount: product.premiumAmnt,
      frequency: product.premFrequency,
      premiumPayingTerm: product.premPayTerm,
      policyTerm: product.policyTerm,
    }));
    setInputValues(initialValues);
  }, [allDetails]);

  const [buttonSectionHeightRef, setButtonSectionHeightRef] = useState(null);

  const ButtonSectionHeight = useMemo(
    () => buttonSectionHeightRef?.offsetHeight,
    [buttonSectionHeightRef]
  );

  useEffect(() => {
    const handleResize = () => {
      setViewportHeight(window.innerHeight);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  let titleSpacing = inputValues.length > 1 ? 160 : 80;

  let spacingHeight = isMobile
    ? HeaderSectionHeight + ButtonSectionHeight + 250 + titleSpacing + 14
    : ButtonSectionHeight + titleSpacing + 40 + 36;

  const isEnabled = agreedData && !isCharacterSpeaking && messageSent;

  const fontFamilyCSS = fontMap[selectedLang?.lang] || "Lato";

  return (
    <div className="flex flex-col-reverse lg:flex-row flex-1  py-3 px-3 md:py-9 md:px-9 lg:px-8 xl:px-14 gap-4 md:gap-6 lg:gap-5 xl:gap-16">
      <MainCard
        mainTitle={t(`Policy Details`)}
        formattedTime={formattedTime}
        FirstSection={
          <div>
            {inputValues.length > 1 && (
              <div className="mt-4 px-2 lg:px-0 mb-3 md:mb-0">
                <p className="text-[12px] lg:text-sm mb-0 text-gray-150">
                  {t("Total Premium Amount(Inclusive of all applicable taxes)")}
                </p>
                <p className="mt-[6px] lg:mt-2 text-base lg:text-lg font-semibold text-black-150">
                  {allDetails?.iciciApplicationDetails.totalPremiumAmnt}₹
                </p>
              </div>
            )}
            <div
              style={{
                // height: `calc(100vh - ${ spacingHeight }px)`,
                height: `${viewportHeight - spacingHeight}px`,
              }}
              className="overflow-y-auto scrollable-content scrollbar scrollbar-primary"
            >
              {inputValues.map((inputValue, index) => (
                <div className="md:mt-8 px-2 md:px-0">
                  {inputValues.length !== 1 && (
                    <p
                      style={{ fontFamily: fontFamilyCSS }}
                      className={`block text-2xl md:text-4xl ${
                        index !== 0 && "mt-6"
                      } md:mt-0 text-black-200 font-extrabold mb-6 md:mb-14`}
                    >
                      {t(`Policy Details ${index + 1}`)}
                    </p>
                  )}
                  <div className="flex">
                    <div>
                      <p
                        htmlFor="planName"
                        className="text-[12px] md:text-sm mb-0 text-gray-150"
                        style={{ fontFamily: fontFamilyCSS }}
                      >
                        {t("Plan Name")}
                      </p>
                      <p
                        id="planName"
                        className="mt-1 text-base md:text-lg font-semibold text-black-150"
                      >
                        {inputValue.planName}
                      </p>
                    </div>
                  </div>
                  <div className="flex mt-3 md:mt-4">
                    <div>
                      <p
                        htmlFor="lifeCover"
                        className="text-[12px] md:text-sm mb-0 text-gray-150"
                        style={{ fontFamily: fontFamilyCSS }}
                      >
                        {t("Life CoverSum assured")}
                      </p>
                      <p
                        id="lifeCover"
                        className="mt-1 text-base md:text-lg font-semibold text-black-150"
                      >
                        {inputValue.lifeCoverSum}₹
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-row md:flex-col">
                    <div className="flex flex-col flex-1 mt-3 md:mt-4">
                      <p
                        htmlFor="premiumAmount"
                        className="text-[12px] md:text-sm mb-0 text-gray-150"
                        style={{ fontFamily: fontFamilyCSS }}
                      >
                        {t("Premium amount")}
                      </p>
                      <p
                        id="premiumAmount"
                        className="mt-1 text-base md:text-lg font-semibold text-black-150"
                      >
                        {inputValue.premiumAmount}₹
                      </p>
                    </div>

                    <div className="flex flex-col flex-1 mt-3 md:mt-4">
                      <p
                        htmlFor="frequency"
                        className="text-[12px] md:text-sm mb-0 text-gray-150"
                        style={{ fontFamily: fontFamilyCSS }}
                      >
                        {t("Frequency")}
                      </p>
                      <p
                        id="frequency"
                        className="mt-1 text-base md:text-lg font-semibold text-black-150"
                      >
                        {inputValue.frequency}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-row md:flex-col">
                    <div className="flex flex-col flex-1 mt-3 md:mt-4">
                      <p
                        htmlFor="premiumPayingTerm"
                        className="text-[12px] md:text-sm mb-0 text-gray-150"
                        style={{ fontFamily: fontFamilyCSS }}
                      >
                        {t("Premium paying term")}
                      </p>
                      <p
                        id="premiumPayingTerm"
                        className="mt-1 text-base md:text-lg font-semibold text-black-150"
                      >
                        {inputValue.premiumPayingTerm}
                        {inputValue.premiumPayingTerm === 1
                          ? " Year"
                          : inputValue.premiumPayingTerm > 1
                          ? " Years"
                          : ""}
                      </p>
                    </div>

                    <div className="flex flex-col flex-1 mt-3 md:mt-4">
                      <p
                        htmlFor="policyTerm"
                        className="text-[12px] md:text-sm mb-0 text-gray-150"
                        style={{ fontFamily: fontFamilyCSS }}
                      >
                        {t("Policy term")}
                      </p>
                      <p
                        id="policyTerm"
                        className="mt-1 text-base md:text-lg font-semibold text-black-150"
                      >
                        {inputValue.policyTerm}
                        {inputValue.policyTerm === 1
                          ? " Year"
                          : inputValue.policyTerm > 1
                          ? " Years"
                          : ""}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        }
        SecondSection={
          <div
            ref={setButtonSectionHeightRef}
            className={`mt-4 xl:mt-3 px-2 md:px-0`}
          >
            <div
              style={{ fontFamily: fontFamilyCSS }}
              className="border-t border-darkGray-100 max-h-14 lg:max-h-16 overflow-y-auto scrollbar scrollbar-primary"
            >
              <p className="text-xs md:text-base italic text-black-150 mt-[10px]">
                {journeyConfigData?.policyConfirmationText}
              </p>
            </div>

            <div className="flex mt-5">
              <div className="flex flex-1">
                <button
                  style={{ fontFamily: fontFamilyCSS }}
                  disabled={!isEnabled}
                  className={`disabled:bg-opacity-15 disabled:cursor-not-allowed bg-orange-150 text-white p-3 rounded-md w-full font-bold md:max-w-max`}
                >
                  {t("I Agree")}
                </button>
                <button
                  style={{ fontFamily: fontFamilyCSS }}
                  className="bg-gray-100 text-black-150 p-3 rounded-md border border-darkGray-150 ml-3 w-full font-bold md:max-w-max"
                >
                  {t("Disagree")}
                </button>
              </div>
              <button
                style={{ fontFamily: fontFamilyCSS }}
                className={`bg-blue-250 text-blue-150 flex items-center justify-center p-3 rounded-md font-bold max-w-max`}
              >
                <img src={imageSrc} alt="icon" className="w-6 h-6" />
              </button>
            </div>
          </div>
        }
      />

      <div
        className={`flex flex-col flex-1 ${
          isMobile && "sticky top-36 md:top-10 lg:top-[15%]"
        }`}
      >
        <RemoteParticipantView />
        <CharacterView
          isRecorder={true}
          characterJoined={characterJoined}
          currentPage={currentPage}
          agreed={agreedData}
          setIsCharacterSpeaking={setIsCharacterSpeaking}
          isCharacterSpeaking={isCharacterSpeaking}
          messageSent={messageSent}
          setMessageSent={setMessageSent}
          message={journeyConfigData?.voiceoverPolicyDetails}
          selectedLang={selectedLang}
        />
      </div>
    </div>
  );
};

export default PolicyDetailSection;
